import { AnalyticsService } from '@alison/analytics/client';
import { type ReactNode, useState } from 'react';
import { z } from 'zod';
import type { CreativeDetailTab } from '../../lib/creative-detail-search.js';
import { useLeadingKpiOptions } from '../../lib/leadingKpi.hooks.js';
import type { CreativeRow } from '../creatives/data/api.js';
import { CreativeDetailDrawer } from '../creatives/detail/CreativeDetailDrawer.js';
import { type CreativeDetailScope, useDetailKpis } from '../creatives/detail/data/api.js';
import type { ReportScope } from './reportMeta.js';
import { creativeRowsOf, type ResultRow } from './resultRows.js';

const RUN_FILTERS_SCHEMA = z.record(z.string(), z.array(z.string()));

export interface CreativeDetail {
  scope: ReportScope;
  leadingKpi: string;
}

interface OpenedCreative {
  creativeId: string;
  tab: CreativeDetailTab;
}

function detailScopeOf(scope: ReportScope): CreativeDetailScope {
  const parsed = RUN_FILTERS_SCHEMA.safeParse(scope.filters ?? {});
  const filters = Object.fromEntries(
    Object.entries(parsed.success ? parsed.data : {}).filter(([, values]) => values.length > 0),
  );
  return {
    dateRange: [scope.startDate, scope.endDate],
    ...(scope.accountIds.length > 0 ? { accountIds: scope.accountIds } : {}),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
  };
}

function OpenCreativeDrawer({
  creatives,
  opened,
  creativeDetail,
  onChange,
}: {
  creatives: CreativeRow[];
  opened: OpenedCreative;
  creativeDetail: CreativeDetail;
  onChange: (opened: OpenedCreative | null) => void;
}): ReactNode {
  const scope = detailScopeOf(creativeDetail.scope);
  const { data: leadingKpiOptions } = useLeadingKpiOptions(scope.accountIds);
  const leadingKpiLabel =
    leadingKpiOptions?.find((option) => option.id === creativeDetail.leadingKpi)?.label ??
    creativeDetail.leadingKpi.toUpperCase();
  const kpis = useDetailKpis(scope.accountIds, creativeDetail.leadingKpi);

  return (
    <CreativeDetailDrawer
      creatives={creatives}
      creativeId={opened.creativeId}
      tab={opened.tab}
      scope={scope}
      kpis={kpis}
      leadingKpiLabel={leadingKpiLabel}
      onOpen={(creativeId) => onChange({ ...opened, creativeId })}
      onTabChange={(tab) => onChange({ ...opened, tab })}
      onClose={() => onChange(null)}
    />
  );
}

export function CreativeDetailOpener({
  workflowId,
  rows,
  creativeDetail,
  tab = 'overview',
  children,
}: {
  workflowId: string;
  rows: ResultRow[];
  creativeDetail: CreativeDetail;
  tab?: CreativeDetailTab;
  children: (onOpen: (row: ResultRow) => void) => ReactNode;
}): ReactNode {
  const [opened, setOpened] = useState<OpenedCreative | null>(null);

  return (
    <>
      {children((row) => {
        if (row.creativeId === null) return;
        AnalyticsService.track('Workflow - Creative Opened', { workflowId });
        setOpened({ creativeId: row.creativeId, tab });
      })}
      {opened === null ? null : (
        <OpenCreativeDrawer
          creatives={creativeRowsOf(rows)}
          opened={opened}
          creativeDetail={creativeDetail}
          onChange={setOpened}
        />
      )}
    </>
  );
}
